import React, { useState, useRef } from 'react';
import { ArrowLeft, Upload, FileSpreadsheet, Check, Loader2, AlertTriangle, Package, Layers, Download } from 'lucide-react';
import { bomAPI } from '../../services/api';

const BOMImportWizard = ({ onBack }) => {
  const [step, setStep] = useState(1); // 1=Upload, 2=Preview, 3=Importing, 4=Done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      setError('');
      const data = await bomAPI.importPreview(file);
      setPreview(data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setStep(3);
      const res = await bomAPI.importConfirm({ boms: preview.boms });
      setResult(res);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
      setStep(2);
    }
  };

  const sortedCats = preview ? Object.entries(preview.categories).sort((a, b) => b[1] - a[1]) : [];

  return (
    <div className="p-5 lg:p-6">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to BOMs
      </button>

      <h1 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>Import BOMs from Excel</h1>
      <p className="text-xs text-gray-500 mb-6">Upload your BOM export file to import all Bill of Materials</p>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {['Upload', 'Preview', 'Import', 'Done'].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 ${step > i + 1 ? 'text-emerald-600' : step === i + 1 ? 'text-blue-700' : 'text-gray-300'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step > i + 1 ? 'bg-emerald-100 text-emerald-700' : step === i + 1 ? 'text-white' : 'bg-gray-100 text-gray-400'}`}
                style={step === i + 1 ? { backgroundColor: '#24549c' } : {}}>
                {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className="text-[11px] font-semibold hidden sm:inline">{s}</span>
            </div>
            {i < 3 && <div className={`flex-1 h-px ${step > i + 1 ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#24549c15' }}>
            <FileSpreadsheet className="w-8 h-8" style={{ color: '#24549c' }} />
          </div>
          <h2 className="text-sm font-bold text-gray-900 mb-1">Upload BOM Excel File</h2>
          <p className="text-[11px] text-gray-500 mb-6">Expected format: FG Item ID, FG UOM, BOM Number, BOM Name, #, Item ID, Item Description, Quantity, Unit, Rate/Unit, Total Cost, ...</p>

          <button onClick={() => bomAPI.downloadTemplate()} data-testid="download-template-btn"
            className="inline-flex items-center gap-2 px-4 py-2 mb-5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors mx-auto">
            <Download className="w-3.5 h-3.5" /> Download Template (.xlsx)
          </button>

          <input type="file" ref={fileRef} accept=".xlsx,.xls" className="hidden"
            onChange={e => { setFile(e.target.files[0]); setError(''); }} />

          {file ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg mb-4">
              <FileSpreadsheet className="w-4 h-4" style={{ color: '#24549c' }} />
              <span className="text-xs font-medium text-gray-800">{file.name}</span>
              <button onClick={() => { setFile(null); fileRef.current.value = ''; }} className="text-gray-400 hover:text-red-500 ml-2">&times;</button>
            </div>
          ) : (
            <button onClick={() => fileRef.current.click()} data-testid="select-file-btn"
              className="px-6 py-3 border-2 border-dashed border-gray-300 rounded-xl text-xs font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors mb-4 block mx-auto">
              Click to select .xlsx file
            </button>
          )}

          <button onClick={handleUpload} disabled={!file || loading} data-testid="parse-btn"
            className="px-6 py-2.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50 flex items-center gap-2 mx-auto"
            style={{ backgroundColor: '#24549c' }}>
            {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Parsing...</> : <><Upload className="w-3.5 h-3.5" /> Parse File</>}
          </button>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-[11px] text-gray-500">BOMs Found</p>
              <p className="text-2xl font-bold" style={{ color: '#24549c' }}>{preview.total_boms}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-[11px] text-gray-500">Finished Goods</p>
              <p className="text-2xl font-bold text-emerald-600">{preview.total_fg_items}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-[11px] text-gray-500">Raw Materials</p>
              <p className="text-2xl font-bold text-purple-600">{preview.total_raw_materials}</p>
            </div>
          </div>

          {/* Categories */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-3">Product Categories</h3>
            <div className="flex flex-wrap gap-2">
              {sortedCats.map(([prefix, count]) => (
                <div key={prefix} className="px-3 py-1.5 bg-gray-50 rounded-lg text-[11px]">
                  <span className="font-semibold text-gray-800">{prefix}</span>
                  <span className="ml-1 text-gray-500">({count})</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sample BOMs */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-900">Preview (first 20 BOMs)</h3>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">BOM #</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">Name</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-500">FG Item</th>
                    <th className="text-center px-4 py-2 font-semibold text-gray-500">Items</th>
                    <th className="text-right px-4 py-2 font-semibold text-gray-500">FG Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.boms.slice(0, 20).map((b, i) => (
                    <tr key={i} className="border-t border-gray-50">
                      <td className="px-4 py-2 font-mono font-semibold">{b.bom_number}</td>
                      <td className="px-4 py-2 text-gray-700 max-w-[200px] truncate">{b.bom_name}</td>
                      <td className="px-4 py-2"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-semibold">{b.fg_item_id}</span></td>
                      <td className="px-4 py-2 text-center">{b.items?.length || 0}</td>
                      <td className="px-4 py-2 text-right font-mono">{b.total_fg_cost ? Number(b.total_fg_cost).toLocaleString('en-IN') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => { setStep(1); setPreview(null); setFile(null); }}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Back
            </button>
            <button onClick={handleConfirm} data-testid="confirm-import-btn"
              className="px-6 py-2.5 text-xs font-semibold text-white rounded-lg flex items-center gap-2 hover:shadow-lg"
              style={{ backgroundColor: '#24549c' }}>
              <Check className="w-3.5 h-3.5" /> Import {preview.total_boms} BOMs
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: '#24549c' }} />
          <p className="text-sm font-semibold text-gray-900">Importing BOMs...</p>
          <p className="text-[11px] text-gray-500 mt-1">This may take a moment</p>
        </div>
      )}

      {/* Step 4: Done */}
      {step === 4 && result && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Import Complete!</h2>
          <p className="text-xs text-gray-600 mb-6">{result.message}</p>
          <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto mb-6">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-xl font-bold text-emerald-700">{result.imported}</p>
              <p className="text-[10px] text-gray-500">Imported</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xl font-bold text-blue-700">{result.updated}</p>
              <p className="text-[10px] text-gray-500">Version Updates</p>
            </div>
          </div>
          <button onClick={onBack} data-testid="done-btn"
            className="px-6 py-2.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
            View BOMs
          </button>
        </div>
      )}
    </div>
  );
};

export default BOMImportWizard;
